/**
 * CRM 목록·보드 다중 선택 + 하단 선택바 + 일괄 변경 (C5/C6).
 * Ctrl/⌘+클릭 토글, Shift+클릭 범위, Space 토글, Ctrl+A, Esc 해제; 선택바의
 * 단계 ▾ · 담당 ▾ · 마감 ▾ · 다음 할 일 ▾ · 삭제 는 /api/crm/{deals|accounts|contacts}/bulk
 * 한 번 호출 → 응답의 before 로 composite(patchCommand…) 를 히스토리에 올리고
 * '12건 단계 변경 · 실행 취소' 토스트를 띄운다. 삭제는 소프트 삭제 + composite(deleteCommand).
 * crm.js 가 setupSelection(ctx) 로 붙인다 (ctx: 아래 사용 필드 참조).
 */
import { showContextMenu } from './context-menu.js';
import { openPeoplePicker } from './people-picker.js';
import { openPillPicker } from './prop-picker.js';
import { attachDatePicker } from './datepicker.js';
import { appConfirm } from './confirm-dialog.js';
import { showToast, patchCommand, deleteCommand, composite, recordQuiet, performUndo } from './calendar-history.js';

const STAGE_COLORS = { lead: 'o8', qualified: 'o1', proposal: 'o4', negotiation: 'o3', won: 'o5', lost: 'o7' };

export function setupSelection(ctx) {
    const { list, board, selBar, kind, L, LJ, post, apiFor, stages, stageLabel, stageOpen, me } = ctx;
    if (!selBar || (!list && !board)) return null;
    const root = () => document.getElementById(list ? 'recList' : 'crmBoard') || list || board;
    const selCount = document.getElementById('crmSelCount');
    const selected = new Set();
    let anchorId = null;
    const fmtN = (p, n) => String(p || '%d').replace('%d', String(n));
    const undoToast = (msg) => showToast(msg, { actionLabel: L.lUndo || '실행 취소', onAction: performUndo });
    const fail = (err) => showToast(err && err.message ? err.message : (L.lFailed || '저장 실패'), { error: true });
    const isTrash = !!(list && list.dataset.trash);
    selBar.dataset.kind = kind;
    selBar.querySelectorAll('[data-for]').forEach((b) => { b.hidden = b.dataset.for !== kind; });
    if (isTrash) selBar.querySelectorAll('[data-act]:not([data-act="clear"]):not([data-act="delete"])').forEach((b) => { b.hidden = true; });
    if (kind === 'account' && !ctx.canManage) { const d = selBar.querySelector('[data-act="delete"]'); if (d) d.hidden = true; }

    const rows = () => Array.from(root().querySelectorAll(list ? '.rec-row[data-id]:not(.crm-follow-row)' : '.kb-card[data-id]'));
    const rowById = (id) => root().querySelector((list ? '.rec-row' : '.kb-card') + '[data-id="' + id + '"]');
    function applySelection() {
        const all = rows();
        all.forEach((r) => { const on = selected.has(r.dataset.id); r.classList.toggle('selected', on); const cb = r.querySelector('.rec-sel'); if (cb) cb.checked = on; });
        root().classList.toggle('has-selection', selected.size > 0);
        const head = document.getElementById('recSelAll');
        if (head) { head.checked = all.length > 0 && selected.size === all.length; head.indeterminate = selected.size > 0 && selected.size < all.length; }
        selBar.hidden = selected.size === 0;
        if (selCount) selCount.textContent = fmtN(LJ.selectedN, selected.size);
        const del = selBar.querySelector('[data-act="delete"]'); if (del && !del.hidden) del.textContent = fmtN(LJ.selDeleteN, selected.size);
        const exp = selBar.querySelector('[data-act="export"]'); if (exp) exp.textContent = fmtN(LJ.exportSelected || '내보내기(%d)', selected.size);
    }
    function clearSelection() { if (!selected.size) return; selected.clear(); applySelection(); }
    function targets() { return Array.from(selected).map(rowById).filter(Boolean); }
    function toggle(row) { if (selected.has(row.dataset.id)) selected.delete(row.dataset.id); else { selected.add(row.dataset.id); anchorId = row.dataset.id; } applySelection(); }
    function selectRange(toId) {
        const all = rows();
        const a = all.findIndex((r) => r.dataset.id === anchorId), b = all.findIndex((r) => r.dataset.id === toId);
        if (a < 0 || b < 0) { selected.add(toId); anchorId = toId; return; }
        for (let i = Math.min(a, b); i <= Math.max(a, b); i++) selected.add(all[i].dataset.id);
    }
    root().addEventListener('change', (e) => {
        if (e.target.id === 'recSelAll') { selected.clear(); if (e.target.checked) rows().forEach((r) => selected.add(r.dataset.id)); applySelection(); return; }
        const cb = e.target.closest('.rec-sel'); if (!cb) return;
        const row = cb.closest('[data-id]'); if (!row) return;
        if (cb.checked) { selected.add(row.dataset.id); anchorId = row.dataset.id; } else selected.delete(row.dataset.id);
        applySelection();
    });
    // 체크박스 클릭은 행 열기로 번지지 않게; Ctrl/Shift+클릭은 선택
    root().addEventListener('click', (e) => {
        if (e.target.closest('.cal-list-check')) { e.stopPropagation(); return; }
        const row = e.target.closest(list ? '.rec-row[data-id]' : '.kb-card[data-id]'); if (!row) return;
        if (e.shiftKey) { e.preventDefault(); e.stopPropagation(); selectRange(row.dataset.id); applySelection(); return; }
        if (e.ctrlKey || e.metaKey) { e.preventDefault(); e.stopPropagation(); toggle(row); }
    }, true);
    const sig = ctx.signal;
    document.addEventListener('keydown', (e) => {
        if (ctx.blocked && ctx.blocked()) return;
        const focusRow = document.activeElement && document.activeElement.closest ? document.activeElement.closest(list ? '.rec-row[data-id]' : '.kb-card[data-id]') : null;
        if (focusRow && !root().contains(focusRow)) return;
        if (e.key === 'Escape') { if (selected.size && !document.querySelector('.prop-pop, .pp-sheet-root, .att-dp-pop, .ctx-menu, .confirm-dialog-overlay.show')) { clearSelection(); e.preventDefault(); } return; }
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'a' && (focusRow || selected.size)) { e.preventDefault(); rows().forEach((r) => selected.add(r.dataset.id)); applySelection(); return; }
        if (e.ctrlKey || e.metaKey || e.altKey) return;
        if (e.key === ' ' && focusRow && !(ctx.spaceIsDone && ctx.spaceIsDone())) { e.preventDefault(); toggle(focusRow); return; }
        if ((e.key === 'Delete' || e.key === 'Backspace') && selected.size && !isTrash) { e.preventDefault(); bulkDelete(); }
    }, { signal: sig });
    document.addEventListener('calendar:refreshed', applySelection, { signal: sig });

    // ── 일괄 변경 ──
    const base = kind === 'account' ? '/api/crm/accounts/bulk' : kind === 'contact' ? '/api/crm/contacts/bulk' : '/api/crm/deals/bulk';
    function bulkPatch(fields, label, afterFields) {
        const els = targets(); if (!els.length) return Promise.resolve();
        const ids = els.map((r) => r.dataset.id);
        const body = new URLSearchParams(fields); ids.forEach((id) => body.append('ids', id));
        return post(base, body).then((res) => {
            const updated = res.updated || [];
            const after = afterFields || fields;
            const cmds = [];
            (res.before || []).forEach((b) => {
                const before = Object.assign({}, b); delete before.id;
                const keys = Object.keys(after);
                const bf = {}; keys.forEach((k) => { if (k in before) bf[k] = before[k]; });
                if (fields.expected_close_delta_days) { const u = updated.find((x) => x.id === b.id); if (u) cmds.push(patchCommand(b.id, { expected_close: b.expected_close }, { expected_close: u.expected_close }, apiFor(kind))); return; }
                if (fields.stage) bf.sort_order = b.sort_order;
                cmds.push(patchCommand(b.id, bf, after, apiFor(kind)));
            });
            updated.forEach((d) => { const el = rowById(d.id); if (el && ctx.paint) ctx.paint(el, d); });
            if (cmds.length) recordQuiet(composite(cmds));
            const n = updated.length;
            let msg = fmtN(label, n);
            if (res.skipped && res.skipped.length) msg += ' · ' + fmtN(LJ.bulkSkipped, res.skipped.length);
            if (n) undoToast(msg); else showToast(msg, { error: true });
            clearSelection();
        }).catch(fail);
    }
    function bulkDelete() {
        const els = targets().filter((r) => kind !== 'deal' || r.dataset.canDelete || ctx.canManage); if (!els.length) { showToast(LJ.noPermissionDelete, { error: true }); return; }
        const run = () => {
            const ids = els.map((r) => r.dataset.id);
            els.forEach((r) => r.classList.add('fading'));
            const body = new URLSearchParams({ delete: '1' }); ids.forEach((id) => body.append('ids', id));
            post(base, body).then((res) => {
                const done = res.deleted || [];
                els.forEach((r) => { if (done.includes(r.dataset.id)) r.remove(); else r.classList.remove('fading'); });
                if (ctx.afterRemove) ctx.afterRemove();
                if (done.length) recordQuiet(composite(done.map((id) => deleteCommand(id, apiFor(kind)))));
                let msg = fmtN(LJ.bulkDeleted, done.length);
                if (res.skipped && res.skipped.length) msg += ' · ' + fmtN(LJ.bulkSkipped, res.skipped.length);
                undoToast(msg);
                clearSelection();
            }).catch((err) => { els.forEach((r) => r.classList.remove('fading')); fail(err); });
        };
        if (kind === 'account') {
            appConfirm({ message: fmtN(LJ.selDeleteN, els.length) + ' — ' + (L.lDeleteAsk || '삭제할까요?'), okLabel: L.lDelete || '삭제', cancelLabel: L.lCancel || '취소', danger: true }).then((ok) => { if (ok) run(); });
            return;
        }
        run();
    }
    function stageOptions() { return stages.map((s) => ({ label: s.label, color: STAGE_COLORS[s.key] || 'o8' })); }
    function keyOfLabel(lbl) { const s = stages.find((x) => x.label === lbl); return s ? s.key : ''; }
    selBar.addEventListener('click', (e) => {
        const b = e.target.closest('[data-act]'); if (!b) return;
        const act = b.dataset.act;
        if (act === 'clear') { clearSelection(); return; }
        if (act === 'delete') { bulkDelete(); return; }
        if (act === 'export') { if (ctx.exportSelected) ctx.exportSelected(kind, Array.from(selected)); return; }
        if (act === 'owner') {
            openPeoplePicker(b, { values: [], allowMe: true, allowNone: true, labels: ctx.pickerLabels, onChange: (ids) => bulkPatch({ owner_id: ids[0] || '' }, LJ.bulkOwner) });
            return;
        }
        if (act === 'stage') {
            openPillPicker(b, { options: stageOptions(), values: [], neutral: false, required: true, placeholder: LJ.stagePick, onChange: (vals) => {
                const st = keyOfLabel(vals[vals.length - 1] || ''); if (!st) return;
                if (!stageOpen(st) && ctx.openClosePopBulk) { ctx.openClosePopBulk(targets(), st, b, (extra) => bulkPatch(Object.assign({ stage: st }, extra), LJ.bulkStage, Object.assign({ stage: st }, extra))); return; }
                bulkPatch({ stage: st }, LJ.bulkStage);
            } });
            return;
        }
        if (act === 'close') {
            const r = b.getBoundingClientRect();
            showContextMenu(r.left, r.top - 8, [
                { label: LJ.weekPlus, onClick: () => bulkPatch({ expected_close_delta_days: '7' }, LJ.bulkClose) },
                { label: LJ.weekMinus, onClick: () => bulkPatch({ expected_close_delta_days: '-7' }, LJ.bulkClose) },
                { label: L.lTbd || '미정', onClick: () => bulkPatch({ expected_close: '' }, LJ.bulkClose) },
                { label: (L.lPickDate || '날짜 선택') + '…', onClick: () => pickDate(b) },
            ], { placement: 'top' });
            return;
        }
        if (act === 'next') {
            const r = b.getBoundingClientRect();
            const presets = ['today', 'tomorrow', 'week'].map((k) => ({ label: ctx.presetLabel[k], onClick: () => askNext(b, ctx.presetDate(k)) }));
            presets.push({ label: L.lNextNone || '없음', onClick: () => bulkPatch({ next_action: '', next_action_at: '' }, LJ.bulkNext) });
            showContextMenu(r.left, r.top - 8, presets, { placement: 'top' }); // 선택바 위로 펼친다 (메뉴 높이를 재서 자리 잡음)
        }
    });
    function pickDate(anchor) {
        const hidden = document.createElement('input'); hidden.type = 'hidden'; hidden.value = ctx.today || '';
        const btn = document.createElement('button'); btn.type = 'button'; btn.style.cssText = 'position:fixed;width:0;height:0;opacity:0;pointer-events:none;';
        const r = anchor.getBoundingClientRect(); btn.style.left = r.left + 'px'; btn.style.top = r.top + 'px';
        document.body.appendChild(btn); document.body.appendChild(hidden);
        attachDatePicker(btn, hidden, { placeholder: L.lPickDate || '', clearLabel: L.lTbd || '미정', onSet: (v) => { bulkPatch({ expected_close: v || '' }, LJ.bulkClose); setTimeout(() => { btn.remove(); hidden.remove(); }, 0); } });
        btn.click();
    }
    function askNext(anchor, at) {
        if (!ctx.mountPop) { bulkPatch({ next_action: '', next_action_at: at }, LJ.bulkNext); return; }
        const pop = document.createElement('div'); pop.className = 'prop-pop crm-bulk-pop';
        const title = document.createElement('div'); title.className = 'crm-close-pop-title'; title.textContent = '📌 ' + (L.lNext || '다음 할 일') + ' · ' + at; pop.appendChild(title);
        const input = document.createElement('input'); input.type = 'text'; input.className = 'form-input calp-edit'; input.maxLength = 200; input.placeholder = L.lNext || ''; pop.appendChild(input);
        const foot = document.createElement('div'); foot.className = 'crm-close-pop-foot';
        const ok = document.createElement('button'); ok.type = 'button'; ok.className = 'btn btn-primary btn-xs'; ok.textContent = L.lConfirm || '확정'; foot.appendChild(ok); pop.appendChild(foot);
        const confirm = () => { const t = input.value.trim(); if (!t) { input.focus(); showToast(L.lNextRequired || '', { error: true }); return; } ctx.closePop(); bulkPatch({ next_action: t, next_action_at: at }, LJ.bulkNext); };
        ok.addEventListener('click', confirm);
        input.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); confirm(); } });
        ctx.mountPop(pop, anchor); input.focus();
    }
    return { has: () => selected.size > 0, targets, clear: clearSelection, toggle, apply: applySelection, bulkDelete, bulkPatch, selected };
}
